// Client helper to fetch live user-review data from product_review_cache
// (or trigger the edge function to refresh it).

import { supabase } from "@/integrations/supabase/client";
import { allProducts, type Product, type ProductCategory } from "@/data/productDatabase";
import { smoothLiveReviews, type UserReviewsData } from "@/lib/ratingsEngine";
import { invalidateCalibratedRatings } from "@/lib/ratingsPosterior";


const productById = new Map<string, Product>(
  allProducts.map((p) => [p.id, p]),
);
const categoryFor = (id: string): ProductCategory =>
  productById.get(id)?.category ?? "subwoofer";

interface CacheRow {
  product_id: string;
  average_rating: number;
  review_count: number;
  normalized_score: number;
  confidence: "low" | "medium" | "high";
  sources: { name: string; url: string; rating?: number; count?: number }[];
  summary: string | null;
  last_refreshed_at: string;
}

const FUNCTIONS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-product-reviews`;

function rowToData(r: CacheRow, category: ProductCategory, product?: Product): UserReviewsData {
  // Recompute the 0-100 score with the Bayesian-shrinkage model so
  // low-volume products can't outrank high-volume ones by accident.
  // Passing the product lets the engine cap stars against measured
  // reliability / abuse-tolerance / build-quality.
  return smoothLiveReviews(
    {
      averageRating: Number(r.average_rating),
      reviewCount: r.review_count,
      sources: r.sources ?? [],
      summary: r.summary ?? undefined,
      lastRefreshed: r.last_refreshed_at,
    },
    category,
    product,
  );
}

// Read the cache for a batch of products at once. Returns a map keyed by product_id.
export async function getCachedReviews(productIds: string[]): Promise<Record<string, UserReviewsData>> {
  if (productIds.length === 0) return {};
  const { data, error } = await supabase
    .from("product_review_cache")
    .select("*")
    .in("product_id", productIds);
  if (error) {
    console.warn("getCachedReviews failed", error);
    return {};
  }
  const out: Record<string, UserReviewsData> = {};
  for (const row of (data ?? []) as unknown as CacheRow[]) {
    out[row.product_id] = rowToData(row, categoryFor(row.product_id), productById.get(row.product_id));
  }
  return out;
}

// Trigger a refresh in the background (fire-and-forget). Returns the new data
// if the call resolves quickly, otherwise null.
// Requires an authenticated user — the edge function validates the JWT to
// prevent anonymous abuse of paid Firecrawl + AI credits.
export async function refreshReviews(p: Product, force = false): Promise<UserReviewsData | null> {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    if (!token) return null; // anon users can read cache but cannot trigger refresh

    const r = await fetch(FUNCTIONS_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({
        product_id: p.id,
        brand: p.brand,
        model: p.model,
        category: p.category,
        force,
      }),
    });
    if (!r.ok) {
      console.warn("refreshReviews failed", r.status);
      return null;
    }
    const j = await r.json();
    if (!j?.data) return null;
    // Fresh review data invalidates this product's memoised calibrated rating.
    invalidateCalibratedRatings(p.id);
    return rowToData(j.data, p.category, p);

  } catch (e) {
    console.warn("refreshReviews error", e);
    return null;
  }
}

// ─── On-demand refresh (hybrid free tier) ───────────────────────────
// Fires fetch-product-reviews in the background ONLY for products with
// no cache row yet AND no recent failure record. Dedupes per-product
// across the session so we don't hammer Firecrawl when the same card
// mounts repeatedly.
const inFlight = new Set<string>();
const triedThisSession = new Set<string>();

async function getRecentFailures(productIds: string[]): Promise<Set<string>> {
  if (productIds.length === 0) return new Set();
  const { data } = await supabase
    .from("product_review_failures")
    .select("product_id")
    .in("product_id", productIds);
  return new Set((data ?? []).map((r) => r.product_id as string));
}

export async function refreshReviewsIfMissing(
  products: Product[],
  alreadyCached: Record<string, UserReviewsData>,
): Promise<Record<string, UserReviewsData>> {
  const newlyFetched: Record<string, UserReviewsData> = {};
  const candidates = products.filter(
    (p) => !alreadyCached[p.id] && !inFlight.has(p.id) && !triedThisSession.has(p.id),
  );
  if (candidates.length === 0) return newlyFetched;

  // Skip products that already failed recently — don't waste credits.
  const failed = await getRecentFailures(candidates.map((p) => p.id));
  const targets = candidates.filter((p) => !failed.has(p.id));
  // Mark failed ones as "tried" for the session too, so we don't re-query them.
  failed.forEach((id) => triedThisSession.add(id));
  if (targets.length === 0) return newlyFetched;

  await Promise.all(
    targets.map(async (p) => {
      inFlight.add(p.id);
      triedThisSession.add(p.id);
      try {
        const fresh = await refreshReviews(p, false);
        if (fresh) newlyFetched[p.id] = fresh;
      } finally {
        inFlight.delete(p.id);
      }
    }),
  );
  return newlyFetched;
}
